/**
 * The plan-driven onboarding (MAR-933) — every rule about WHICH screens a user sees in `/app/setup`,
 * WHETHER the app sends them there, and WHICH reminders stay behind afterwards.
 *
 * ── ONE PURE MODULE, THREE CALLERS ───────────────────────────────────────────────────────────────
 *
 * The route middleware (`app/middleware/auth.global.ts`, via `useOnboarding`), the setup page and
 * the reminder bubble all read the same functions here, and so does the server when the user enters
 * the optional phase (`initialStepStates`). Each is a function of the profile `/api/profile` already
 * returns and nothing else: the plan's `feature_flags`, the stored onboarding state, the name, and —
 * ONLY while the user has not entered the optional phase — the one-time org `evaluation`. Nothing here
 * fetches, so the whole table is pinned by `tests/unit/onboardingFlow.test.ts` without a server.
 *
 * ── THE TWO PHASES ───────────────────────────────────────────────────────────────────────────────
 *
 * REQUIRED — `org` → `plan` → `licences` → `name`: a founder with an organization and no subscription buys
 * first (MAR-1457). Unchanged fail-closed semantics: a profile we could not
 * resolve sends the user to `org`, never into an app where every request 403s (AlexAmin/marcus#243).
 * A name stays required even after the optional phase was entered: it is not a step one can skip.
 *
 * OPTIONAL — documents, clients, cases. Offered only when the plan has the feature (clients and cases
 * need `case_management`) and the org does not already have the entity. The user ENTERS this phase
 * by leaving the welcome/name screens; the server stamps `onboarding_started_at` and stores which
 * steps it offered. From then on the gate never routes them again — **leaving counts as skipping**:
 * the first time the user is anywhere but `/app/setup` with a step still `offered`, the route middleware
 * records it as `skipped` (`leave`, O4), and it becomes a reminder either way.
 *
 * ── WHY STEP STATES AND NOT A LIST OF WHAT WAS OFFERED ───────────────────────────────────────────
 *
 * A reminder has to tell apart three reasons a step was not done: it was skipped (remind), the org
 * already had the entity (never remind), or the plan lacked the feature when the user onboarded
 * (remind once the plan gains it — the "newly unlocked" case). A list of offered steps collapses the
 * last two; `present` vs an absent key keeps them apart. Deletions do not bring anything back: the
 * stored state is final and nothing re-counts the org once the phase was entered.
 */
import type { PlanFeatureFlags } from '../organizations/planFeatures'
import { TEAM_COUNT_CAP, type OnboardingTeamCounts } from './team'
import type { StoredCredential } from './credential'

export const OPTIONAL_STEPS = ['documents', 'clients', 'cases'] as const
export type OptionalStep = (typeof OPTIONAL_STEPS)[number]

/**
 * - `offered` — shown to the user, not yet answered. Leaving mid-flow turns it into `skipped` (the
 *   `leave` action, O4); until that is recorded — a closed tab, say — it reads exactly like `skipped`.
 * - `done` / `skipped` — the user's answer on that screen.
 * - `present` — not shown, because the org already had the entity when the user entered.
 * An ABSENT key means the plan lacked the feature at the time — see the file header.
 */
export type OptionalStepState = 'offered' | 'done' | 'skipped' | 'present'
export type OnboardingStepStates = Partial<Record<OptionalStep, OptionalStepState>>

/**
 * What `users.onboarding_steps` actually holds: the step states, plus the ids of the EXISTING clients
 * picked in the clients step (§20, F2) — picking creates nothing, so this is the only record of it,
 * and it is what lets a reload at the cases step still offer them (`GET /api/onboarding/clients`).
 * Kept in the same jsonb so it needs no column, and cleared with the steps by a replay. The picks are
 * server-side only: the profile's `steps` carries the step states and nothing else.
 */
export type StoredOnboardingSteps = OnboardingStepStates & {
  picked_clients?: string[]
  /**
   * The clients the clients step CREATED (F3). "Created in this onboarding" means created through that
   * step and nothing else — a client the user made in another tab is neither created nor picked here.
   */
  created_clients?: string[]
  /** The cases the cases step created (O7) — what lets Done still name them after a reload. */
  created_cases?: string[]
  /** The name screen was part of this run (O7): a reload keeps it in the counter and on Done. */
  name_step?: boolean
  /**
   * The steps `leave` turned from `offered` into `skipped` (O4) — LEFT, rather than skipped on their
   * screen. Coming back to `/app/setup` on purpose still resumes at the first of them; answering one
   * on its screen takes it off.
   */
  left_steps?: OptionalStep[]
  /**
   * The carnet or the verified student proof from the name screen (MAR-1146, `./credential.ts`). Not a
   * step state: entering the optional phase and a replay both keep it, so it is asked once.
   */
  credential?: StoredCredential
  /**
   * When the welcome email was claimed (MAR-31, ISO timestamp): stamped under the row lock the first
   * time the user enters the optional phase, before the email is sent. Like the credential it is not
   * a step state — entering and a replay both keep it — so the email goes out at most once per user.
   * Server-only: the profile never reports it.
   */
  welcome_email_sent_at?: string
  /**
   * "Asigna tus licencias" was answered (MAR-1404, the `licences_answered` action): the founder took a licence
   * or opted out ("Sin licencia"). Like the credential it is not a step state — entering and a replay keep it —
   * so the step is asked once. Server-only: the profile reports it as `licence_step: 'not_required'`.
   */
  licences_answered?: boolean
}

export function isOptionalStep(value: unknown): value is OptionalStep {
  return typeof value === 'string' && (OPTIONAL_STEPS as readonly string[]).includes(value)
}

export type FlowScreen = 'welcome' | 'name' | OptionalStep | 'done'

/** Which welcome copy the setup page shows — the only thing the user's history changes. */
export type WelcomeVariant = 'new' | 'existing' | 'existing_complete' | 'joined'

/**
 * The look at the org, computed server-side ONLY for a user who has not entered the optional phase
 * (`server/services/onboarding/index.ts`). Null for everyone else, which is what keeps an onboarded
 * user's `/api/profile` free of these reads. Existence checks only: it runs on every profile read until
 * the user enters, so the joined welcome's exact numbers are NOT here — they are `GET
 * /api/onboarding/team` (`shared/onboarding/team.ts`), fetched once by that screen.
 */
export interface OnboardingEvaluation {
  /** Any document — folders, drafts and trashed items excluded; workflow outputs count. */
  has_documents: boolean
  /** Any client of the organization. */
  has_clients: boolean
  has_cases: boolean
  /** The user joined an organization somebody else created (rather than creating it). */
  joined: boolean
  /** The user has used Marcus before (any billed activity) — an existing user, not a new signup. */
  returning: boolean
}

export interface OnboardingProfileState {
  /** Entered the optional phase (`users.onboarding_started_at`). Never routed again once true. */
  started: boolean
  /** Reached the Done screen (`users.onboarding_finished_at`). */
  finished: boolean
  steps: OnboardingStepStates
  dismissed_reminders: OptionalStep[]
  evaluation: OnboardingEvaluation | null
  /**
   * The name screen was part of this run (O7) — recorded by the name screen's `start`. Optional so an
   * older cached profile still reads; a replay clears it with the steps.
   */
  name_step?: boolean
  /** The steps stored as skipped because the user LEFT them (O4) — where coming back resumes. */
  left_steps?: OptionalStep[]
  /**
   * Whether this user owes the plan step (MAR-1047): `required` when they are an admin who founded
   * the organization and billing reports no subscription; `unknown` when billing could not be asked,
   * which the gate treats as `required` (fail closed). Absent on an older cached profile, and on every
   * profile that is not an unstarted founder's: no plan step.
   */
  plan_step?: 'required' | 'not_required' | 'unknown'
  /**
   * Whether this user owes "Asigna tus licencias" (MAR-1404): `required` for a founding admin of an
   * organization WITH a subscription who holds no licence — since MAR-1399 a checkout buys empty
   * licences, so that is every founder who has just paid. Computed with `plan_step`, only before the
   * optional phase (`server/services/onboarding/planStep.ts`). Absent: not owed.
   */
  licence_step?: 'required' | 'not_required'
  /**
   * The founder answered "Asigna tus licencias" with "Sin licencia" and still holds no licence (MAR-1404): no
   * optional step needs offering or reminding, since every one of them needs a licence. Absent: false.
   */
  no_licence?: boolean
  /**
   * A carnet or a verified law-student proof is stored (MAR-1146). Only the boolean leaves the server.
   * Optional so an older cached profile still reads; the gate treats its absence as `false`.
   */
  has_credential?: boolean
}

/** The slice of `UserProfile` these rules read. */
export interface GateProfile {
  organization_name: string | null
  display_name: string | null
  /** Set while a superadmin acts as another organization — who never triggers or writes onboarding. */
  org_override_id: string | null
  feature_flags: PlanFeatureFlags
  onboarding: OnboardingProfileState
}

export type OnboardingGate = 'org' | 'name' | 'plan' | 'licences' | 'welcome' | null

/**
 * What the gate cannot read off the profile yet (MAR-1404): the founder answered "Asigna tus licencias" in this
 * session. The answer is stored (`licences_answered`) and the next profile read reports `licence_step:
 * 'not_required'`; this keeps the gate from showing the step again while that write is in flight.
 */
export interface GateSession {
  licencesAnswered?: boolean
}

/** The status half of `useUserProfile` — declared here so this module does not import app code. */
export type GateProfileStatus = 'idle' | 'ok' | 'unavailable'

/** Clients and cases need case management; documents are always available. */
export function planAllowsStep(step: OptionalStep, flags: PlanFeatureFlags): boolean {
  // `?.`: a partial profile (see `stateOf` below) may carry no flags at all.
  return step === 'documents' ? true : flags?.case_management === true
}

function hasEntity(step: OptionalStep, evaluation: OnboardingEvaluation): boolean {
  if (step === 'documents') return evaluation.has_documents
  if (step === 'clients') return evaluation.has_clients
  return evaluation.has_cases
}

/**
 * What the server stores the moment the user enters the optional phase.
 *
 * A null evaluation (the counts failed) offers every step the plan allows: an extra, skippable screen
 * is a far smaller harm than a step that silently never appears and never reminds.
 */
export function initialStepStates(flags: PlanFeatureFlags, evaluation: OnboardingEvaluation | null, noLicence = false): OnboardingStepStates {
  const states: OnboardingStepStates = {}
  // MAR-1404: a founder who answered "Asigna tus licencias" with "Sin licencia" holds no licence, and every
  // route behind these steps needs one: none is offered, and none is stored (a licence later unlocks them).
  if (noLicence) return states
  for (const step of OPTIONAL_STEPS) {
    if (!planAllowsStep(step, flags)) continue
    states[step] = evaluation && hasEntity(step, evaluation) ? 'present' : 'offered'
  }
  return states
}

/**
 * A profile with NO onboarding state — an older cached shape, or a partial answer from a stub or a
 * proxy. Every rule reads through this rather than `profile.onboarding` directly: a throw inside a
 * computed freezes whatever component reads it, which is worse than any onboarding behaviour. The
 * gate treats the absence as "nothing to route for" (the optional phase is optional); reminders
 * treat it as "nothing to remind of".
 */
const NO_STATE: OnboardingProfileState = { started: false, finished: false, steps: {}, dismissed_reminders: [], evaluation: null }
function stateOf(profile: GateProfile): OnboardingProfileState {
  return profile.onboarding ?? NO_STATE
}

function hasName(profile: GateProfile): boolean {
  return !!profile.display_name?.trim()
}

/**
 * The route gate. `null` means "let them into the app".
 *
 * `idle` still returns null (every caller awaits a load before asking), and every other unresolved
 * status fails CLOSED to `org` — unchanged from the gate this replaces (AlexAmin/marcus#243).
 */
export function gateFor(status: GateProfileStatus | string, profile: GateProfile | null, session: GateSession = {}): OnboardingGate {
  if (status === 'idle') return null
  if (status !== 'ok' || !profile) return 'org'
  // MAR-1388: visiting /app without an organization goes directly to the buying page (`plan`),
  // where creating the organization is part of purchasing a plan (ahead of `name`).
  if (!profile.organization_name?.trim()) return 'plan'
  // A superadmin acting as another organization is looking at SOMEBODY ELSE's workspace: onboarding
  // them there would count that org's entities and write state onto the superadmin's own row.
  if (profile.org_override_id) return null
  // MAR-1457 (Alex, 2026-11-01): a founder with an organization and no subscription buys FIRST — before
  // "Asigna tus licencias", the name and the credential — so a reload after a refused quote (`quote_stale`)
  // returns to the plan, not to a name form with nothing bought. MAR-1047: a founder buys a plan (or redeems
  // a voucher) before the welcome; joiners and members never get `required` (the server decides that,
  // before onboarding starts and only then). `unknown` fails closed to `required`.
  const plan = profile.onboarding?.plan_step
  if (profile.onboarding && !profile.onboarding.started && (plan === 'required' || plan === 'unknown')) return 'plan'
  // MAR-1404: a founder who has just paid holds none of the licences bought (MAR-1399) until assigned
  // one: "Asigna tus licencias" comes right after paying, ahead of the name step, so a reload returns to it.
  if (profile.onboarding && !profile.onboarding.started && profile.onboarding.licence_step === 'required' && !session.licencesAnswered) return 'licences'
  if (!hasName(profile)) return 'name'
  if (!profile.onboarding) return null
  if (!profile.onboarding.started) {
    // MAR-1146: the name screen also asks for a carnet or a student proof — of a user who has not
    // entered the optional phase or finished, so somebody onboarded before this rule is never sent back.
    // Missing counts as absent (fail closed, like `plan_step: 'unknown'`).
    if (!profile.onboarding.finished && profile.onboarding.has_credential !== true) return 'name'
    return 'welcome'
  }
  return null
}

/** The optional steps this user walks, in order. */
function optionalScreens(profile: GateProfile): OptionalStep[] {
  const onboarding = stateOf(profile)
  const flags = profile.feature_flags
  const states = onboarding.started ? onboarding.steps : initialStepStates(flags, onboarding.evaluation, onboarding.no_licence === true)
  return OPTIONAL_STEPS.filter(step => {
    const state = states[step]
    return state === 'offered' || state === 'done' || state === 'skipped'
  })
}

/**
 * Every post-gate screen of the setup page, in order. `name` runs as a gate step before `plan` and
 * `welcome` (`gateFor`, MAR-1145), so `flowFor` starts at `'welcome'` followed by the plan's optional
 * steps and `'done'`.
 */
export function flowFor(profile: GateProfile): FlowScreen[] {
  return [
    'welcome',
    ...optionalScreens(profile),
    'done',
  ]
}

/** Where a (re)load of `/app/setup` starts: the welcome before entering, the first unanswered step after. */
export function resumeScreen(profile: GateProfile): FlowScreen {
  const onboarding = stateOf(profile)
  if (!onboarding.started) return 'welcome'
  if (!hasName(profile)) return 'name'
  // An unanswered step, or one stored as skipped only because the user left it (O4): coming back on
  // purpose resumes there. A step skipped ON its screen is answered — a reload on Done stays on Done.
  const left = onboarding.left_steps ?? []
  return OPTIONAL_STEPS.find(step => onboarding.steps[step] === 'offered' || left.includes(step)) ?? 'done'
}

export function welcomeVariant(profile: GateProfile): WelcomeVariant {
  const evaluation = stateOf(profile).evaluation
  if (evaluation?.joined) return 'joined'
  const hasAnything = !!evaluation && (evaluation.has_documents || evaluation.has_clients || evaluation.has_cases)
  const existing = !!evaluation && (evaluation.returning || hasAnything)
  if (!existing) return 'new'
  return optionalScreens(profile).length ? 'existing' : 'existing_complete'
}

/**
 * Whether a route path is the setup page. Trailing slashes are ignored: `/app/setup/` renders the
 * same page, and treating it as elsewhere made loading it mid-flow count as LEAVING — `leave` then
 * stored the unanswered steps as skipped while the flow was still on screen (PR #1072 tester).
 */
export function isSetupPath(path: string): boolean {
  return path.replace(/\/+$/, '') === '/app/setup'
}

/**
 * Whether leaving `/app/setup` still has something to record (O4): the user entered the optional
 * phase and a step is still `offered`. The route middleware then sends `leave` once, which stores those
 * steps as `skipped` — "anything not done when the user leaves is skipped". Never for a superadmin
 * acting as another organization (who has no onboarding there), nor a profile without the state.
 */
export function hasUnansweredSteps(profile: GateProfile): boolean {
  const onboarding = profile.onboarding
  if (!onboarding?.started || profile.org_override_id) return false
  return OPTIONAL_STEPS.some(step => onboarding.steps?.[step] === 'offered')
}

/**
 * Which `onboarding.welcome.<key>.body` the welcome shows (O3). The joined subtitle ("Tu equipo ya
 * trabaja aquí. Solo falta lo tuyo.") says the team already has work in Marcus, so it is shown only
 * when the team line (`teamSummary`) has something in it; otherwise a neutral `joinedEmpty`.
 */
export function welcomeBodyKey(variant: WelcomeVariant, team: readonly TeamSummaryEntry[]): WelcomeVariant | 'joinedEmpty' {
  if (variant === 'joined' && !team.length) return 'joinedEmpty'
  return variant
}

/** One entity of the joined welcome's line. `capped`: the org has more than `count`, shown "1000+". */
export interface TeamSummaryEntry { step: OptionalStep; count: number; capped: boolean }

/**
 * What the joined welcome says the team already has, in step order: one entry per entity the org
 * holds and the plan includes (clients and cases need `case_management`). `counts` is what the
 * welcome fetched from `GET /api/onboarding/team` — null until it arrives, and for anyone the line is
 * not for. Empty for anyone who is not joining, for anyone past the welcome (no evaluation), and when
 * the team has nothing yet — the setup page shows the line only when this has something in it.
 * Informational only: the steps themselves follow the same rules as for everyone (Alex, 2026-09-25,
 * option b).
 */
export function teamSummary(profile: GateProfile, counts: OnboardingTeamCounts | null): TeamSummaryEntry[] {
  const evaluation = stateOf(profile).evaluation
  if (!evaluation?.joined || !counts) return []
  return OPTIONAL_STEPS
    .filter(step => planAllowsStep(step, profile.feature_flags))
    .map(step => ({ step, count: Math.min(counts[step], TEAM_COUNT_CAP), capped: counts[step] > TEAM_COUNT_CAP }))
    .filter(entry => entry.count > 0)
}

/** One line of the welcome's promise — an `onboarding.welcome.bullets.*` key. */
export type WelcomeBullet =
  | 'name' | 'documents' | 'ready'
  | 'clientsCasesFromDocuments' | 'clientsFromDocuments' | 'casesFromDocuments'
  | 'clientsCases' | 'clients' | 'cases'
  | 'whereDocuments' | 'whereCases' | 'whereChat'

/**
 * What the welcome promises: one line per step this user will actually walk, and nothing else
 * (MAR-933 F7). `screens` is the snapshot `flowFor` gave the setup page.
 *
 * The clients/cases line comes in six wordings because it must neither promise a step that will not
 * come (a joined user whose team already has clients reads only about cases) nor say "from them"
 * when no documents step precedes it to give "them" a meaning. The already-set-up welcome has no
 * steps to promise, so it says where things are instead — clients and cases only on a plan with them.
 */
export function welcomeBullets(variant: WelcomeVariant, screens: readonly FlowScreen[], flags: PlanFeatureFlags): WelcomeBullet[] {
  const has = (s: FlowScreen) => screens.includes(s)
  if (variant === 'existing_complete') {
    // No optional step to promise, but the name step may still follow ("Paso 1 de 2" with nothing
    // about step 2 was the audit's r3 finding): it is promised first, then where things are.
    return [
      ...(has('name') ? ['name' as const] : []),
      'whereDocuments', ...(flags?.case_management ? ['whereCases' as const] : []), 'whereChat',
    ]
  }
  const out: WelcomeBullet[] = []
  if (has('name')) out.push('name')
  if (has('documents')) out.push('documents')
  const fromDocuments = has('documents')
  if (has('clients') && has('cases')) out.push(fromDocuments ? 'clientsCasesFromDocuments' : 'clientsCases')
  else if (has('clients')) out.push(fromDocuments ? 'clientsFromDocuments' : 'clients')
  else if (has('cases')) out.push(fromDocuments ? 'casesFromDocuments' : 'cases')
  if (!out.length || (variant === 'joined' && !screens.some(isOptionalStep))) out.push('ready')
  return out
}

/**
 * The reminders still to show, in step order — one per step the user skipped (or left unanswered),
 * plus one per step a later plan upgrade unlocked. Hidden for a step the plan no longer has, and for
 * a step the user dismissed. Nothing before the optional phase was entered: until then the user is
 * still inside onboarding, and a superadmin acting as another org never sees one.
 */
export function pendingReminders(profile: GateProfile): OptionalStep[] {
  const onboarding = stateOf(profile)
  const flags = profile.feature_flags
  if (!onboarding.started || profile.org_override_id) return []
  // MAR-1404: an opted-out founder can do none of them; once they hold a licence, the unlocked ones remind.
  if (onboarding.no_licence) return []
  return OPTIONAL_STEPS.filter(step => {
    if (!planAllowsStep(step, flags)) return false
    if (onboarding.dismissed_reminders.includes(step)) return false
    const state = onboarding.steps[step]
    return state !== 'done' && state !== 'present'
  })
}
